/**
 * StreamingMessageBubble - Real-time streaming assistant message display
 *
 * Shows content as it streams in with:
 * - Animated cursor at the end of content
 * - Tool execution indicator
 * - Graceful transition to final state
 */

import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { MarkdownContent } from './MarkdownContent'
import { stripStreamingDirectiveTags } from '@/lib/fabric/stripDirectiveTags'
import type { CitationResponse } from '@/api/types'

interface StreamingMessageBubbleProps {
  /** Accumulated content from streaming */
  content: string
  /** Citations collected so far */
  citations: CitationResponse[]
  /** Name of currently executing tool — no longer streamed; the traces
   *  sub-resource is the audit surface for tool calls. */
  currentTool?: string | null
  /** Whether streaming is in progress */
  isStreaming: boolean
  /** Error message if streaming failed */
  error: string | null

  /** Callback to retry failed message */
  onRetry?: () => void
  /** Whether a retry is in progress */
  isRetrying?: boolean
}

/**
 * Tool name to user-friendly label mapping
 */
const TOOL_LABELS: Record<string, string> = {
  medication_lookup: 'Searching medications',
  snomed_lookup: 'Looking up terminology',
  check_drug_interaction: 'Checking interactions',
  expert_search: 'Finding experts',
  fhir_lookup: 'Looking up resources',
}

/**
 * Animated cursor that appears at the end of streaming content
 */
function StreamingCursor() {
  return (
    <span
      className="inline-block w-0.5 h-[1.2em] bg-primary/80 ml-0.5 align-middle animate-cursor-blink"
      aria-hidden="true"
    />
  )
}

/**
 * Tool execution indicator shown while tools are running
 */
function ToolIndicator({ toolName }: { toolName: string }) {
  const { t } = useTranslation()
  const label = TOOL_LABELS[toolName] || t('chat.streaming.searching')

  return (
    <div className="flex items-center gap-2 mt-2 py-2 px-3 bg-neutral-50 rounded-lg border border-neutral-200/50">
      <Loader2 className="w-4 h-4 text-primary animate-spin" />
      <span className="text-sm text-neutral-600">{label}...</span>
    </div>
  )
}

export const StreamingMessageBubble = memo(function StreamingMessageBubble({
  content,
  citations,
  currentTool,
  isStreaming,
  error,
  onRetry,
  isRetrying = false,
}: StreamingMessageBubbleProps) {
  const { t } = useTranslation()

  // Only show cursor when actively streaming content (not during tool execution)
  const showCursor = isStreaming && !currentTool && content.length > 0

  // Streaming incomplete banner (partial content + error)
  const hasPartialContent = !isStreaming && error && content.length > 0

  // Memoize content rendering to avoid re-parsing markdown on every delta.
  // Directive tags are stripped here too, but this is no longer where the
  // stream lands: under the async-turn (202) contract this component renders
  // only until the POST resolves, and `content` is empty for all of it. The
  // deltas go through appendTurnDelta into the placeholder message, which
  // MessageBubble renders — the strip that matters is there. Kept because
  // `content` is still populated on the retry path, and because a component
  // that CAN show streamed text should not depend on which path currently
  // feeds it.
  const renderedContent = useMemo(() => {
    if (!content) return null
    const visible = stripStreamingDirectiveTags(content)
    if (!visible) return null
    return <MarkdownContent content={visible} />
  }, [content])

  return (
    <div
      data-testid="streaming-message-bubble"
      data-role="assistant"
      className="flex flex-col items-start"
    >

      {/* DESIGN.md: flat assistant bubble — white/canvas + 1px hairline,
          no drop shadow. Matches MessageBubble. */}
      <div
        className={cn(
          "max-w-[85%] px-4 py-3 rounded-message rounded-bl-md",
          "bg-white text-neutral-900 border border-neutral-200"
        )}
      >
        {/* Streaming content with cursor */}
        <div className="text-md leading-relaxed">
          {content ? (
            <div className="relative">
              {renderedContent}
              {showCursor && <StreamingCursor />}
            </div>
          ) : isStreaming && !currentTool ? (
            // Initial state before any content arrives
            <div className="flex items-center gap-1.5">
              <span
                className="w-2 h-2 rounded-full bg-primary/60 animate-typing-bounce"
                style={{ animationDelay: '0ms' }}
              />
              <span
                className="w-2 h-2 rounded-full bg-primary/60 animate-typing-bounce"
                style={{ animationDelay: '200ms' }}
              />
              <span
                className="w-2 h-2 rounded-full bg-primary/60 animate-typing-bounce"
                style={{ animationDelay: '400ms' }}
              />
            </div>
          ) : null}
        </div>

        {/* Tool execution indicator */}
        {currentTool && <ToolIndicator toolName={currentTool} />}

        {/* Partial content banner (streaming interrupted) */}
        {hasPartialContent && (
          <div className="mt-3 py-2 px-3 bg-warning-50 rounded-lg border border-warning-200/50 flex items-start justify-between gap-2">
            <p className="text-sm text-warning-800 flex-1">
              {t('chat.errors.streamingIncomplete')}
            </p>
            {onRetry && (
              <button
                onClick={onRetry}
                disabled={isRetrying}
                aria-label={t('chat.errors.retryButton')}
                className="text-sm font-medium text-warning-600 hover:text-warning-900 disabled:opacity-50 whitespace-nowrap"
              >
                {isRetrying ? t('chat.errors.retrying') : t('chat.errors.retryButton')}
              </button>
            )}
          </div>
        )}

        {/* Error message (no partial content) */}
        {error && !hasPartialContent && (
          <div className="mt-2 py-2 px-3 bg-red-50 rounded-lg border border-red-200/50 flex items-start justify-between gap-2">
            <p className="text-sm text-red-600 flex-1">{error}</p>
            {onRetry && (
              <button
                onClick={onRetry}
                disabled={isRetrying}
                aria-label={t('chat.errors.retryButton')}
                className="text-sm font-medium text-red-700 hover:text-red-900 disabled:opacity-50 whitespace-nowrap"
              >
                {isRetrying ? t('chat.errors.retrying') : t('chat.errors.retryButton')}
              </button>
            )}
          </div>
        )}

        {/* Citation count indicator (shown during streaming before final citations) */}
        {citations.length > 0 && isStreaming && (
          <div className="mt-2 pt-2 border-t border-neutral-200/30">
            <span className="text-xs text-citation-600">
              {t('chat.streaming.citationsFound', { count: citations.length })}
            </span>
          </div>
        )}
      </div>

    </div>
  )
})
